import type { FormEventHandler } from 'react'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Select from 'components/Common/Form/Input/Select'
import moment from 'moment'
import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { getData as getCountries } from 'country-list'
import { formatTourV2VariationDetailCounts } from 'lib/tours/extractTourV2VariationDetails'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { sendPrivateRequestForTourV2 } from 'api/tour/tourV2'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import type { PACKAGE_UPGRADE_VALUES } from 'constants/tours'
import { getOfferPackageUpgradeDisplayTextMap } from 'constants/tours'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

const FormFieldsContainer = styled.div`
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-column-gap: ${rem(12)};
    grid-template-columns: 1fr 1fr;

    .full-width-column {
      grid-column: span 2;
    }
  }
`

const AlertContainer = styled(Group)`
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const ROOM_CAPACITIES: Array<App.RoomCapacity> = [{
  adults: 2,
  children: 0,
  infants: 0,
  teenagers: 0,
}]

interface MappedStateProps {
  firstName?: string
  lastName?: string
  phone?: string
  phonePrefix?: string
  email?: string
  countryCode: string
  customerId?: string
}
interface Props {
  offer: App.Tours.TourV2Offer
  /**
   * The departure making a private request for
   * Will default the tour/dates selected based on this departure
   * and not allow changing them
   */
  selectedDeparture?: App.Tours.TourV2OfferDeparture
  defaultTravellers: Array<App.Occupants>
}

interface VariationOption {
  id: string
  tourOptionId: string
  value: string
  price?: number
}

function PrivateRequestFormModal(props: Props & MappedStateProps) {
  const {
    firstName,
    lastName,
    phone,
    phonePrefix,
    countryCode,
    email,
    offer,
    customerId,
    selectedDeparture,
    defaultTravellers,
  } = props

  const [submitting, setSubmitting] = useState<boolean>(false)
  const variations = useMemo(() => Object.values(offer.variations), [offer])
  const startDateToBook = useMemo(() => {
    return selectedDeparture ? undefined : moment().add(3, 'weeks')
  }, [selectedDeparture])

  const variationOptions = useMemo<Array<VariationOption>>(() => {
    return variations.map((variation) => ({
      id: variation.id,
      tourOptionId: variation.fkTourOptionId,
      value: `${variation.name} - ${formatTourV2VariationDetailCounts(variation).variationDetailsCountText}`,
      price: variation.privateRequestFromPrice,
    }))
  }, [variations])

  const [selectedVariation, setSelectedVariation] = useState<VariationOption>(() => {
    if (selectedDeparture) {
      return variationOptions.find(option => option.id === selectedDeparture.fkVariationId) ?? variationOptions[0]
    }
    return variationOptions[0]
  })

  const packageUpgradesOptions = useMemo<Array<{ value: string, label: string }>>(() => {
    return offer.variations[selectedVariation.id]?.availablePackageUpgrades?.map((packageOption) => ({
      value: packageOption.packageOption,
      label: packageOption.packageOptionDisplayName || (getOfferPackageUpgradeDisplayTextMap(offer.id).get(packageOption.packageType as PACKAGE_UPGRADE_VALUES) ?? ''),
    })) ?? []
  }, [offer.id, offer.variations, selectedVariation.id])

  const maxChildAge = variations[0].maxChildPriceAge
  const isLoggedIn = !!customerId
  const intl = useIntl()
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(defaultTravellers)
  const handleOccupancies = useCallback((occupancies: Array<App.Occupants>) => setOccupancies(occupancies), [setOccupancies])

  const [fromPrice, setFromPrice] = useState(variations[0].privateRequestFromPrice)
  const handleVariationChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>((e) => {
    const variationSelected = variationOptions.find(variationOption => variationOption.id === e.target.value)
    if (variationSelected) {
      setSelectedVariation(variationSelected)
      setFromPrice(variationSelected.price)
    }
  }, [variationOptions])

  const modalState = useModalElementContext<unknown>()

  const tourUrl = useMemo(() => {
    if (selectedVariation) {
      const urls = window.location.href.split('/')
      urls.splice(-1, 1, selectedVariation.id)
      return urls.join('/')
    }
    return window.location.href
  }, [selectedVariation])

  const showModal = useContext(ModalContext)
  const submitRequest = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()

    setSubmitting(true)

    // if we end up needing to login, the 'currentTarget' gets removed from the event
    // So save our form reference
    const form = e.currentTarget
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        modalState.resolve(undefined)
        return
      }
      // they logged in, continue and send the request
    }

    const requestPrivateTourWithoutTourOptionId = formToObject<Omit<App.Tours.RequestPrivateTour, 'tourOptionId'>>(form)

    const fullPayload: App.Tours.RequestPrivateTour = {
      ...requestPrivateTourWithoutTourOptionId,
      variation: selectedVariation.value,
      tourOptionId: selectedVariation.tourOptionId,
      occupancy: occupancies.map(occupant => ({
        adults: occupant.adults,
        children: occupant.children,
        infants: occupant.infants,
        childrenAge: occupant.childrenAge,
      })),
    }
    let response
    try {
      response = await sendPrivateRequestForTourV2(fullPayload)
      showSnackbar('Private Request Submitted', 'success')
    } catch {
      showSnackbar('Sorry. Private Request Failed', 'critical')
    } finally {
      setSubmitting(false)
      modalState.resolve(response)
    }
  }, [isLoggedIn, selectedVariation, occupancies, modalState, showModal])

  const bodyText = intl.formatMessage({
    id: 'GhLSI+zWai',
    defaultMessage: 'Please let us know your details and our Tours Concierge will start tailoring your private tour.',
    description: 'Body text in private tour request form modal',
  })
  const firstNameLabel = intl.formatMessage({
    id: 'DTeo2uoGgk',
    defaultMessage: 'First name',
    description: 'Label for first name input in private tour request form',
  })
  const lastNameLabel = intl.formatMessage({
    id: 'ZThtG0kqg5',
    defaultMessage: 'Last name',
    description: 'Label for last name input in private tour request form',
  })
  const emailLabel = intl.formatMessage({
    id: 'dkpxexYYOX',
    defaultMessage: 'Email address',
    description: 'Label for email address input in private tour request form',
  })
  const mobileLabel = intl.formatMessage({
    id: 'fEN33PQEEK',
    defaultMessage: 'Mobile number',
    description: 'Label for mobile number input in private tour request form',
  })
  const tourSelectLabel = intl.formatMessage({
    id: 'A10bGrUttJ',
    defaultMessage: 'Which tour are you interested in?',
    description: 'Label for tour selection dropdown in private tour request form',
  })
  const upgradesLabel = intl.formatMessage({
    id: 'dB/JBqO8ee',
    defaultMessage: 'Available upgrades',
    description: 'Label for package upgrades dropdown in private tour request form',
  })
  const travelDateLabel = intl.formatMessage({
    id: 'YPLEENDDkf',
    defaultMessage: 'When are you looking to travel?',
    description: 'Label for travel date picker in private tour request form',
  })
  const selectDatePlaceholder = intl.formatMessage({
    id: '3uZK17qV0s',
    defaultMessage: 'Select your date',
    description: 'Placeholder for travel date picker in private tour request form',
  })
  const travellingWithLabel = intl.formatMessage({
    id: 'HB5LPR4Srv',
    defaultMessage: 'Who are you travelling with?',
    description: 'Label for occupancy picker in private tour request form',
  })
  const pricingDisclaimer = intl.formatMessage({
    id: 'T5F8urDV1M',
    defaultMessage: 'Pricing subject to seasonal availability and will be confirmed by your Tour Concierge.',
    description: 'Pricing disclaimer in private tour request form',
  })
  const commentsLabel = intl.formatMessage({
    id: '8yCW3tJowd',
    defaultMessage: 'Any other questions or comments?',
    description: 'Label for comments text area in private tour request form',
  })
  const commentsPlaceholder = intl.formatMessage({
    id: 'ZH/d0YP7n+',
    defaultMessage: 'eg. Do I need a vaccine? I wish to travel with children ...',
    description: 'Placeholder for comments text area in private tour request form',
  })
  const submitButtonText = intl.formatMessage({
    id: 'Bq+NBCY06k',
    defaultMessage: 'Submit request',
    description: 'Submit button text in private tour request form',
  })
  const modalTitle = intl.formatMessage({
    id: '2fKTu0de7d',
    defaultMessage: 'Request a private tour',
    description: 'Title for the private tour request form modal',
  })

  return <ModalBase size="S">
    <form onSubmit={submitRequest}>
      <ModalHeader title={modalTitle} />
      <ModalBody>
        <ModalContent>
          <LoadingIndicator floating visible={submitting} />
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              {bodyText}
            </BodyText>
            <VerticalSpacer gap={16}>
              <FormFieldsContainer>
                <HiddenInput name="tourId" value={offer.id} />
                {customerId && <HiddenInput name="customerId" value={customerId} />}
                <HiddenInput name="tourUrl" value={tourUrl} />
                <TextInput
                  label={firstNameLabel}
                  name="firstName"
                  required
                  placeholder={firstNameLabel}
                  defaultValue={firstName}
                />
                <TextInput
                  label={lastNameLabel}
                  name="lastName"
                  required
                  placeholder={lastNameLabel}
                  defaultValue={lastName}
                />
                <TextInput
                  className="full-width-column"
                  type="email"
                  label={emailLabel}
                  name="email"
                  placeholder={emailLabel}
                  required
                  defaultValue={email}
                />
                <PhoneInput
                  label={mobileLabel}
                  name="phone"
                  id="mobileNumber"
                  required
                  defaultPhoneNumber={phone}
                  defaultPhonePrefix={phonePrefix}
                  prefixCountry={countryCode}
                />
                <Select
                  label="Your home country"
                  name="countryCode"
                  defaultValue={countryCode}
                  required
                >
                  {getCountries().map(country =>
                    <option key={country.code} value={country.code}>{country.name}</option>,
                  )}
                </Select>
                {(variationOptions.length === 1 || selectedDeparture) && <HiddenInput
                  name="variation"
                  value={selectedVariation.id}
                />}
                <Select
                  className="full-width-column"
                  label={tourSelectLabel}
                  name="variation"
                  defaultValue={selectedVariation.id}
                  onChange={handleVariationChange}
                  required
                  disabled={variationOptions.length === 1 || !!selectedDeparture}
                >
                  {variationOptions.map(variationOption =>
                    <option key={variationOption.id} value={variationOption.id}>{variationOption.value}</option>,
                  )}
                </Select>
                {packageUpgradesOptions.length === 1 && <HiddenInput name="packageOption" value={packageUpgradesOptions[0].value} />}
                {packageUpgradesOptions.length > 1 && <Select
                  className="full-width-column"
                  label={upgradesLabel}
                  name="packageOption"
                  defaultValue={packageUpgradesOptions[0].value}
                  required
                >
                  {packageUpgradesOptions.map(packageOption =>
                    <option key={packageOption.value} value={packageOption.value}>{packageOption.label}</option>,
                  )}
                </Select>}
                <DatePickerInput
                  className="full-width-column"
                  label={travelDateLabel}
                  name="planningTravelDate"
                  min={startDateToBook?.toDate()}
                  defaultMonth={startDateToBook?.month()}
                  defaultValue={selectedDeparture?.startDate}
                  displayFormat="DD/MM/YYYY"
                  placeholder={selectDatePlaceholder}
                  endIcon={<LineCalendarIcon />}
                  disabled={!!selectedDeparture}
                  required
                />
                <VerticalSpacer gap={4} className="full-width-column">
                  <BodyText weight="bold" variant="medium">{travellingWithLabel}</BodyText>
                  <OccupancyPicker
                    compact
                    name="occupancy"
                    maxRooms={10}
                    capacities={ROOM_CAPACITIES}
                    occupancies={occupancies}
                    childrenAgeLabel="at time of departure"
                    maxChildAge={maxChildAge}
                    onChange={handleOccupancies}
                    enableMultiRoom
                    childVariant="none"
                    adultLabelType="traveller"
                    removeAdultAgeCaption
                  />
                </VerticalSpacer>
              </FormFieldsContainer>
              <AlertContainer direction="horizontal" horizontalAlign="space-evenly">
                <BodyTextBlock variant="small" startIcon={<LineInfoCircleIcon />}>{pricingDisclaimer}</BodyTextBlock>
              </AlertContainer>
              <TextArea
                name="comment"
                noValidationSpacing
                resize="vertical"
                label={commentsLabel}
                placeholder={commentsPlaceholder}
                maxLength={150}
              />
            </VerticalSpacer>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: submitButtonText,
          type: 'submit',
        }}
      >
        <Group direction="horizontal" horizontalAlign="start" fullWidth>
          {fromPrice && <div>
            <PriceRowPriceCaption>From</PriceRowPriceCaption>
            <PriceRowPrice
              size="M"
              price={fromPrice}
              saleUnit="person"
            />
            <PriceRowTaxesAndFees />
            <PriceRowCaption>{intl.formatMessage({
              id: 'EV0vp7iF0v',
              defaultMessage: 'Twin Share',
              description: 'Price row caption for twin share price',
            })}
            </PriceRowCaption>
          </div>}
        </Group>
      </ModalFooter>
    </form>
  </ModalBase>
}

const mapStateToProps = (state: App.State): MappedStateProps => {
  return {
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    phone: state.auth.account.phone,
    phonePrefix: state.auth.account.phonePrefix,
    email: state.auth.account.email,
    countryCode: state.geo.currentRegionCode,
    customerId: state.auth.account.memberId,
  }
}

export default connect(mapStateToProps)(PrivateRequestFormModal)
